import React, { useState, useEffect, useRef, useCallback } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Button, Tooltip } from "antd";
import isEqual from "react-fast-compare";

const RichTextEditor = ({
  attributeFields = [],
  initialContent = "",
  onChange,
  readOnly = false,
}) => {
  const [editorData, setEditorData] = useState(initialContent || "");
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const prevAttributeFieldsRef = useRef([]);
  const [isDroppingOver, setIsDroppingOver] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const tokenInsertQueue = useRef([]);

  // Get token style based on field name or type
  const getTokenStyle = (field) => {
    // First check source-based styling
    const source = field?.source || "attribute";

    switch (source) {
      case "x-axis":
        return {
          bg: "var(--x-axis-bg, #e6f7ff)",
          border: "var(--x-axis-border, #91d5ff)",
          color: "var(--x-axis-text, #1890ff)",
        };
      case "breakdown-by":
        return {
          bg: "var(--breakdown-bg, #f9f0ff)",
          border: "var(--breakdown-border, #d3adf7)",
          color: "var(--breakdown-text, #722ed1)",
        };
      case "y-series":
        return {
          bg: "var(--y-series-bg, #f6ffed)",
          border: "var(--y-series-border, #b7eb8f)",
          color: "var(--y-series-text, #52c41a)",
        };
      default:
        // Fallback to field-specific styling
        switch (field?.attribute || field?.name) {
          default:
            // Type-based fallback styling
            return field?.type === "varchar" || field?.type === "string"
              ? {
                  bg: "var(--varchar-bg, #e6f7ff)",
                  border: "var(--varchar-border, #91d5ff)",
                  color: "var(--varchar-text, #1890ff)",
                }
              : {
                  bg: "var(--numeric-bg, #f6ffed)",
                  border: "var(--numeric-border, #b7eb8f)",
                  color: "var(--numeric-text, #52c41a)",
                };
        }
    }
  }; // Create custom tokens for attributes
  const insertAttributeToken = useCallback(
    (
      attributeNameOrField,
      attributeType = null,
      attributeSource = null,
      traceName = null,
      value = null
    ) => {
      // If editor is not ready, queue the token for later
      if (!isEditorReady || !editorRef?.current) {
        tokenInsertQueue?.current?.push({
          attributeNameOrField,
          attributeType,
          attributeSource,
          traceName,
          value,
        });
        console.warn("Editor not ready, token queued");
        return;
      }

      let attributeName, fieldType, fieldSource, fieldTraceName, fieldValue;

      // Handle case where first argument is a complete field object
      if (
        typeof attributeNameOrField === "object" &&
        attributeNameOrField !== null
      ) {
        const fieldObj = attributeNameOrField;
        attributeName = fieldObj?.attribute || fieldObj?.name;
        fieldType = fieldObj?.type || "varchar";
        fieldSource = fieldObj?.source || "attribute";
        fieldTraceName = fieldObj?.trace_name || attributeName;
        fieldValue = fieldObj?.value;
      } else {
        // Handle case where arguments are provided individually
        attributeName = attributeNameOrField;
        fieldType = attributeType || "varchar";
        fieldSource = attributeSource || "attribute";
        fieldTraceName = traceName || attributeName;
        fieldValue = value;
      }
      // Insert as plain text token
      const token = `{{attribute:${fieldTraceName || attributeName}}}`;
      if (!editorRef?.current || !editorRef?.current?.model) {
        // Editor not ready, queue the token
        tokenInsertQueue?.current?.push({
          attributeNameOrField,
          attributeType,
          attributeSource,
          traceName,
          value,
        });
        console.warn("Editor not ready (model missing), token queued");
        return;
      }
      editorRef?.current?.model?.change((writer) => {
        editorRef?.current?.model?.insertContent(
          writer?.createText(token),
          editorRef?.current?.model?.document?.selection
        );
      });
    },
    [isEditorReady]
  ); // Track previous attribute fields to detect new ones
  // Track previous attribute fields to detect new ones
  useEffect(() => {
    const prevFields = prevAttributeFieldsRef?.current;

    // Guard against empty attribute fields
    if (!attributeFields || attributeFields?.length === 0) {
      prevAttributeFieldsRef.current = [];
      return;
    }

    // Don't auto-insert on first load (only when new fields are added after initialization)
    const isFirstLoad = prevFields?.length === 0 && attributeFields?.length > 0;
    if (isFirstLoad) {
      // console.log("First load of attribute fields - not auto-inserting");
      prevAttributeFieldsRef.current = [...attributeFields];
      return;
    }

    // Find new fields that were just added
    const newFields = attributeFields?.filter(
      (field) =>
        field &&
        (field?.attribute || field?.name) &&
        !prevFields.some(
          (prevField) =>
            (prevField?.attribute &&
              field?.attribute &&
              prevField?.attribute === field?.attribute) ||
            (prevField?.name &&
              field?.name &&
              prevField?.name === field?.name) ||
            (prevField?.trace_name &&
              field?.trace_name &&
              prevField?.trace_name === field?.trace_name)
        )
    );

    // console.log("New fields detected:", newFields);

    // If there are newly added fields, insert them as tokens (only if editor is ready)
    if (newFields?.length > 0) {
      // Insert the first new field automatically
      const firstNewField = newFields[0];

      if (firstNewField) {
        // console.log("Auto-inserting new field:", firstNewField);
        // Use the field object format for consistency
        insertAttributeToken(firstNewField);
      }
    }

    // Update the ref with the current fields
    prevAttributeFieldsRef.current = [...attributeFields];
  }, [attributeFields, insertAttributeToken]);

  useEffect(() => {
    // Update content when initialContent prop changes
    if (initialContent !== undefined && initialContent !== null) {
      setEditorData(typeof initialContent === "string" ? initialContent : "");
    }
  }, [initialContent]);
  // Handle attribute drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    // Set dropEffect to show this is a valid drop target
    e.dataTransfer.dropEffect = "copy";
    setIsDroppingOver(true);
    // Log every 10th dragover event to avoid console spam
    if (Math.random() < 0.1) {
      logDataTransfer(e, "DragOver");
    }
  };

  // Handle attribute drag leave
  const handleDragLeave = () => {
    setIsDroppingOver(false);
  }; // Handle attribute drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDroppingOver(false);
    // console.log(
    //   "Drop event triggered on element:",
    //   e.currentTarget?.tagName || "unknown element"
    // );

    // Log all available data in the dataTransfer object
    logDataTransfer(e, "Drop");

    // Ensure we position the cursor at the drop location before inserting
    if (editorRef?.current && editorRef?.current?.editing?.view?.document) {
      try {
        const viewDocument = editorRef?.current?.editing?.view?.document;
        const domDropPosition = e?.target?.closest(".ck-editor__editable");

        if (domDropPosition) {
          // Try to get position from drop coordinates
          const domDocument = domDropPosition.ownerDocument;
          const domRange = domDocument.caretRangeFromPoint(
            e.clientX,
            e.clientY
          );

          if (domRange && editorRef?.current?.model) {
            // console.log("Found DOM range at drop position");
            editorRef?.current?.model?.change((writer) => {
              // Try to convert DOM position to model position
              writer?.setSelection(domRange, 0);
            });
          }
        }
      } catch (posError) {
        console.error("Error setting cursor position:", posError);
      }
    }

    try {
      // Check multiple data formats in a specific order
      let data;
      let format;

      // Try JSON format first
      if (e?.dataTransfer?.types?.includes("application/json")) {
        data = e.dataTransfer.getData("application/json");
        format = "application/json";
      }

      // Then try plain text
      if (!data && e?.dataTransfer?.types?.includes("text/plain")) {
        data = e.dataTransfer.getData("text/plain");
        format = "text/plain";
      }

      // Finally try generic text
      if (!data && e?.dataTransfer?.types?.includes("text")) {
        data = e.dataTransfer.getData("text");
        format = "text";
      }

      // console.log(`Dropped data from ${format || "unknown"} format:`, data);

      if (data) {
        // Try to parse the data as JSON
        try {
          const parsedData = JSON.parse(data);
          // console.log("Successfully parsed JSON data:", parsedData);

          // Handle the field object directly for consistent format
          if (typeof parsedData === "object" && parsedData !== null) {
            // Use the field object directly with our enhanced token inserter
            insertAttributeToken(parsedData);
            return;
          }
        } catch (parseError) {
          // console.log(
          //   "Failed to parse JSON, trying alternative format:",
          //   parseError
          // );

          // If it's not valid JSON, try to extract attribute information
          const draggedId = data?.split("#");
          if (draggedId?.length >= 2) {
            const attName = draggedId[0];
            const attType = draggedId[1];
            let source = "attribute";
            let traceName = attName;

            // Handle both direct attribute drags and drags from the axis panels
            // For drags from EditAxisPanel sections (x_axis, breakdown_by, y_series)
            if (draggedId?.includes("x_axis")) {
              source = "x-axis";
            } else if (draggedId?.includes("breakdown_by")) {
              source = "breakdown-by";
            } else if (draggedId?.includes("y_series")) {
              source = "y-series";
            }

            // If dragging from a panel with additional metadata
            if (draggedId?.length >= 3) {
              // Check if there's a trace name specified
              traceName = draggedId[2] || traceName;
            }

            // console.log("Using string format data:", {
            //   attName,
            //   attType,
            //   source,
            //   traceName,
            // });

            // Create a complete field object
            const fieldObj = {
              attribute: attName,
              name: attName,
              type: attType || "varchar",
              source: source || "attribute",
              trace_name: traceName || attName,
            };

            // Insert using the object format for consistency
            insertAttributeToken(fieldObj);
            return;
          }
        }
      }

      console.warn("Could not extract valid attribute data from drop event");
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  }; // Function to ensure drag and drop works on the editor
  const setupEditorDragHandlers = useCallback(
    (editorElement) => {
      if (!editorElement) return;

      // console.log("Setting up drag handlers on editor element:", editorElement);

      // Find the editable area which might be capturing our drag events
      const editableElement = editorElement.querySelector(
        ".ck-editor__editable"
      );
      if (editableElement) {
        // console.log("Found editable element:", editableElement);

        // Remove any existing event listeners first to prevent duplicates
        const newDragOver = (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDragOver(e);
        };

        const newDragLeave = (e) => {
          e.preventDefault();
          handleDragLeave();
        };

        const newDrop = (e) => {
          e.preventDefault();
          e.stopPropagation();
          // console.log("Drop detected on editable area");
          handleDrop(e);
        };

        // Need to use capture phase to intercept events before CKEditor
        editableElement.addEventListener("dragover", newDragOver, true);
        editableElement.addEventListener("dragleave", newDragLeave, true);
        editableElement.addEventListener("drop", newDrop, true);

        // Store cleanup function in ref
        return () => {
          editableElement.removeEventListener("dragover", newDragOver, true);
          editableElement.removeEventListener("dragleave", newDragLeave, true);
          editableElement.removeEventListener("drop", newDrop, true);
        };
      }
    },
    [handleDragOver, handleDragLeave, handleDrop]
  );
  // Effect to apply our drag handlers after the editor is ready
  useEffect(() => {
    if (editorContainerRef?.current) {
      const cleanup = setupEditorDragHandlers(editorContainerRef.current);
      return () => {
        if (cleanup && typeof cleanup === "function") {
          cleanup();
        }
      };
    }
  }, [setupEditorDragHandlers]);

  // Function to check what data is available in a DataTransfer object
  const logDataTransfer = (e, label = "DataTransfer") => {
    const dt = e?.dataTransfer;
    if (!dt) {
      // console.log(`${label}: No DataTransfer object available`);
      return;
    }

    // console.log(`${label}: Available types:`, dt.types);

    try {
      if (dt.types?.includes("text/plain")) {
        // console.log(`${label}: text/plain:`, dt.getData("text/plain"));
      }

      if (dt.types?.includes("text")) {
        // console.log(`${label}: text:`, dt.getData("text"));
      }

      if (dt.types?.includes("application/json")) {
        // console.log(
        //   `${label}: application/json:`,
        //   dt.getData("application/json")
        // );
      }
    } catch (error) {
      console.error(`Error reading DataTransfer:`, error);
    }
  };

  // Flush token queue when editor becomes ready
  useEffect(() => {
    if (
      isEditorReady &&
      editorRef?.current &&
      tokenInsertQueue?.current?.length > 0
    ) {
      tokenInsertQueue?.current?.forEach((args) => {
        insertAttributeToken(
          args?.attributeNameOrField,
          args?.attributeType,
          args?.attributeSource,
          args?.traceName,
          args?.value
        );
      });
      tokenInsertQueue.current = [];
    }
  }, [isEditorReady, insertAttributeToken]);

  return (
    <div
      className={`rich-text-editor-container ${
        isDroppingOver ? "dropping-over" : ""
      }`}
      ref={editorContainerRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={(e) => {
        handleDrop(e);
      }}
    >
      {/* Hide CKEditor branding icon */}
      <style>{`.ck-powered-by { display: none !important; }`}</style>
      {!readOnly && (
        <div className="drag-hint text-gray font-size13 mb-2">
          Drag attributes from above to insert dynamic values
        </div>
      )}
      <div
        className={`editor-wrapper ${isDroppingOver ? "highlight-drop" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => {
          handleDrop(e);
        }}
      >
        <CKEditor
          editor={ClassicEditor}
          data={typeof editorData === "string" ? editorData : ""}
          disabled={readOnly}
          config={{
            toolbar: readOnly
              ? []
              : [
                  "heading",
                  "|",
                  "bold",
                  "italic",
                  "link",
                  "bulletedList",
                  "numberedList",
                  "|",
                  "indent",
                  "outdent",
                  "|",
                  "blockQuote",
                  //   "insertTable",
                  "|",
                  "undo",
                  "redo",
                ],
            htmlSupport: {
              allow: [
                {
                  name: /^(span|div|section|iframe|br|p)$/, // Add any custom HTML tags here
                  classes: true,
                  attributes: true,
                  styles: true,
                },
              ],
            },
          }}
          onReady={(editor) => {
            // console.log("Editor is ready");

            // Store the editor reference for later use
            editorRef.current = editor;
            setIsEditorReady(true);

            // Ensure the editor is initialized and content is loaded
            editor?.editing?.view?.focus();

            // Attach drag/drop listeners directly to the editor instance
            editor?.editing?.view?.document?.on(
              "dragover",
              (evt, data) => {
                data?.stop();

                if (data?.originalEvent) {
                  data?.originalEvent?.preventDefault();
                  // console.log("CKEditor dragover intercepted");
                  // Pass to our handler
                  handleDragOver(data?.originalEvent);
                }
              },
              { priority: "highest" }
            );

            editor?.editing?.view?.document?.on(
              "drop",
              (evt, data) => {
                data?.stop();

                if (data?.originalEvent) {
                  data?.originalEvent?.preventDefault();
                  data?.originalEvent?.stopPropagation();
                  // console.log("CKEditor drop intercepted");
                  // Pass to our handler
                  handleDrop(data?.originalEvent);
                }
              },
              { priority: "highest" }
            );

            // Add custom handling for non-editable elements (our tokens)
            editor?.editing?.view?.document?.on(
              "mousedown",
              (evt, data) => {
                // If clicking on our token, prevent default CKEditor behavior
                if (
                  data?.target &&
                  data?.target?.hasClass &&
                  (data?.target?.hasClass("attribute-token") ||
                    data?.target?.findAncestor("span.attribute-token"))
                ) {
                  // console.log("Clicked on token - preventing edit");
                  data?.preventDefault();
                  evt?.stop();
                }
              },
              { priority: "highest" }
            );

            // Setup drag handlers on the editor DOM element
            if (
              editor &&
              editor?.ui &&
              editor?.ui?.view &&
              editor?.ui?.view?.element
            ) {
              setupEditorDragHandlers(editor?.ui?.view?.element);
            }

            // Create a global method to check if editor is ready for debugging
            window.__isEditorReady = true;
          }}
          onChange={(event, editor) => {
            const data = editor?.getData();
            // console.log("Editor data changed:", data);
            setEditorData(data);
            onChange && onChange(data);
          }}
        />
      </div>{" "}
      {/* Available Fields : Hidden */}
      {false && !readOnly && attributeFields && attributeFields?.length > 0 && (
        <div className="attribute-chips mt-3">
          <p className="text_regular mb-2">Available Fields:</p>
          <div className="d-flex flex-wrap">
            {Array.isArray(attributeFields) &&
              attributeFields?.map((field, index) => {
                const source = field?.source || "attribute";
                return (
                  <Tooltip
                    key={index}
                    title={`Click to insert ${field?.trace_name} at cursor position.`}
                    // attribute${
                    //   source !== "attribute" ? ` (${source})` : ""
                    // }
                  >
                    <div
                      className={`attribute-chip attribute-chip-${field?.type} attribute-chip-${source} mr-2 mb-2 cursor-pointer`}
                      onClick={() => {
                        // console.log("Chip clicked:", field);
                        insertAttributeToken(
                          field?.attribute,
                          field?.type,
                          source,
                          field?.trace_name,
                          field?.value
                        );
                      }}
                      draggable
                      onDragStart={(e) => {
                        const fieldData = {
                          attribute: field?.attribute,
                          name: field?.attribute, // Include both for compatibility
                          type: field?.type || "varchar",
                          source: source,
                          trace_name: field?.trace_name || field?.attribute,
                          value: field?.value,
                        };

                        // console.log("Drag started with data:", fieldData);
                        e?.dataTransfer?.setData(
                          "text",
                          JSON.stringify(fieldData)
                        );
                        // Set effectAllowed to show this is a copy operation
                        e.dataTransfer.effectAllowed = "copy";

                        // For debugging: set a custom data format to check if it's preserved
                        e?.dataTransfer?.setData(
                          "application/json",
                          JSON.stringify(fieldData)
                        );
                      }}
                    >
                      {source !== "attribute" && (
                        <span className="source-badge-mini mr-1">
                          {source === "x-axis"
                            ? "X"
                            : source === "breakdown-by"
                            ? "B"
                            : "Y"}
                        </span>
                      )}
                      {field?.trace_name || field?.attribute}
                    </div>
                  </Tooltip>
                );
              })}
          </div>
          <div className="attribute-help text-gray font-size12 mt-2">
            Drag fields into the editor or click on them to insert at cursor
            position.
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(RichTextEditor, isEqual);
